'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'

export type SwitchProps = Omit<
  React.ComponentProps<'input'>,
  'type' | 'role' | 'checked' | 'onChange' | 'className'
> & {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  className?: string
}

/**
 * An on/off setting of Alumnia UI (a native `input` with `role="switch"`,
 * like the system's native checkboxes): Space toggles it, the screen reader
 * says "on"/"off", and the visible label is the caller's, linked by `id`.
 * The track is 44 by 24 px inside a 44 px target, in `ink` when on and in
 * `line-control` when off, with the focus ring of every control. Movement
 * of the knob stops under reduced motion.
 */
export function Switch({ checked, onCheckedChange, disabled, className, ...props }: SwitchProps) {
  return (
    <span
      data-slot="switch"
      className={cn(
        'relative inline-grid min-h-en-control shrink-0 place-items-center',
        disabled && 'opacity-45',
        className
      )}
    >
      <input
        {...props}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onCheckedChange(event.target.checked)}
        className={cn(
          'peer h-6 w-11 cursor-pointer appearance-none rounded-en-full border border-en-line-control bg-en-line-control',
          'transition-colors duration-200 checked:border-en-ink checked:bg-en-ink motion-reduce:transition-none',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus',
          'disabled:cursor-not-allowed'
        )}
      />
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute top-1/2 left-0.5 size-5 -translate-y-1/2 rounded-en-full bg-en-surface shadow-en-card',
          'transition-transform duration-200 peer-checked:translate-x-5 motion-reduce:transition-none'
        )}
      />
    </span>
  )
}
